<template>
  <component :is="formType" :class="cssClasses" @submit.prevent="handleSubmit">
    <slot name="errors" :errors="errors">
      <ErrorList v-if="errors" :errors="errors?.__all__" />
    </slot>
    <div v-if="defaultGroup" class="inside">
      <template
        v-for="field of defaultGroup.fields"
        :key="field.name"
        :class="{ [direction]: true, [field.type || 'text']: true }"
      >
        <slot
          :name="field.type"
          :field="field"
          :object="props.modelValue"
          :defaults="defaults"
          :updateField="(data) => update(field, data)"
          :update="update"
        >
          <AutoField
            @update:modelValue="updateProxy($event)"
            :form-component="formComponent"
            :field="field"
            :errors="props.errors"
            :defaults="props.defaults"
            :modelValue="props.modelValue"
          />
          <div v-if="field.help" class="help">{{ field.help }}</div>
          <div v-if="errors && errors[field.name]" class="error">
            <template v-if="errors[field.name] instanceof Array">
              <div v-for="error of errors[field.name]">
                {{ error }}
              </div>
            </template>
            <template v-else>
              {{ errors[field.name] }}
            </template>
          </div>
        </slot>
      </template>
    </div>
    <Accordion>
      <AccordionTab
        v-for="group of grouped.filter((g) => g.name !== 'default')"
        :header="group.name"
      >
        <div class="inside">
          <template
            v-for="field of group.fields"
            :key="field.name"
            :class="{ [direction]: true, [field.type || 'text']: true }"
          >
            <slot
              :name="field.type"
              :field="field"
              :object="props.modelValue"
              :defaults="defaults"
              :updateField="(data) => update(field, data)"
              :update="update"
            >
              <AutoField
                @update:modelValue="emit('update:modelValue', $event)"
                :form-component="formComponent"
                :field="field"
                :defaults="props.defaults"
                :modelValue="props.modelValue"
              />
            </slot>
            <div v-if="field.help" class="help">{{ field.help }}</div>
          </template>
        </div>
      </AccordionTab>
    </Accordion>
    <template v-if="!props.sub" class="row buttons">
      <slot>
        <div class="button-pre-cell" />
        <div class="button-cell">
          <Button type="submit" class="btn btn-primary">
            {{ actionButtonText }}
          </Button>
        </div>
      </slot>
    </template>
  </component>
</template>

<script setup lang="ts">
import copy from "@virgodev/bazaar/functions/copy";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import Button from "primevue/button";
import type { Component } from "vue";
import { computed, onMounted, watch } from "vue";
import AutoField from "./AutoField.vue";
import ErrorList from "./ErrorList.vue";
import { FieldDef, updateModelValue } from "./defs";

const props = withDefaults(
  defineProps<{
    fields: FieldDef[];
    modelValue: Object;
    errors?: Object;
    defaults?: Object;
    formComponent?: Component;
    sub?: boolean;
    actionButtonText?: string;
  }>(),
  { actionButtonText: "Submit" },
);

const emit = defineEmits(["submit", "update:modelValue"]);

const defaults = computed(() => defaults || {});
const formType = computed(() => (props.sub ? "div" : "form"));
const grouped = computed(() => {
  const groups = [];
  for (const field of props.fields) {
    const name = field.group || "default";
    let group = groups.find((g) => g.name === name);
    if (!group) {
      group = { name, fields: [] };
      groups.push(group);
    }
    group.fields.push(field);
  }
  return groups;
});
const defaultGroup = computed(() => {
  return grouped.value.find((g) => g.name === "default");
});
const cssClasses = computed(() => {
  const retval = {
    "auto-form": true,
    "sub-form": props.sub,
  };
  return retval;
});

let timeout = 0;
watch(
  () => props.fields,
  async () => {
    let local = 0;
    await new Promise((resolve) => {
      clearTimeout(timeout);
      timeout = window.setTimeout(resolve, 100);
      local = timeout;
    });
    if (local === timeout) {
      applyDefaults();
    }
  },
  { deep: true },
);

onMounted(async () => {});

function updateProxy(value) {
  emit("update:modelValue", value);
}

function update(field, value, key = null) {
  emit(
    "update:modelValue",
    updateModelValue(props.modelValue, field, value, key),
  );
}

function applyDefaults() {
  const data = copy(props.modelValue);
  let usedDefault = false;
  for (const field of props.fields.filter((f) => f.default)) {
    if (!data[field.name]) {
      data[field.name] = field.default;
      usedDefault = true;
    }
  }
  if (usedDefault) {
    emit("update:modelValue", data);
  }
}

function handleSubmit($event) {
  $event.stopImmediatePropagation();
  emit("submit");
}
</script>

<style scoped>
.auto-form > .error {
  text-align: center;
  max-width: 600px;
  margin: auto;
}
.auto-form .inside {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 5px;
  padding: 5px;
  max-width: 600px;
  margin: auto;
}
.auto-form label,
.auto-form :deep(label) {
  text-align: right;
  padding-top: 10px;
  white-space: wrap;
  max-width: 30vw;
}
:deep(.p-inputtext, .p-password) {
  width: 100%;
  min-width: 1px;
}
.error {
  grid-column: span 1;
  padding: 3px;
}
</style>
